import { CellContext } from '@tanstack/react-table';
import { PauseSquareIcon, ContinueSquareIcon, TrashSquareIcon } from '../../../../../lib/icons';
import { ActionsMenuCell, ActionMenuItem } from '../../shared/actions-menu-cell';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationDialog } from '../../../compound';
import { useState } from 'react';
import { Bonus } from '@pxtra-client-lib-legacy/gen';
import { useDeleteBonus, usePatchBonus } from '../../../../../queries/bonus/mutations';
import Util from '../../../../../misc/util';

export function ActionsCellBonus({ info }: Readonly<{ info: CellContext<Bonus, unknown> }>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const isPayoutMonthInPast = Util.isMonthFieldInPast(info.row.original.payout_month);

  const { mutate: deleteBonus } = useDeleteBonus({
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['bonuses'] });
      enqueueSnackbar(t('bonus:cancelSuccess'), {
        variant: 'detailedSuccess',
        details: t('bonus:canceledBonus', { bonus: info.row.original.title })
      });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(
        t('bonus:cancelError', {
          error: error instanceof Error ? error.message : 'Unknown error'
        }),
        {
          variant: 'error'
        }
      );
    }
  });

  const { mutate: patchBonus } = usePatchBonus();

  const pause = (pause: boolean) => {
    patchBonus(
      { bonusId: info.row.original.id, requestBody: { status: pause ? 'PAUSED' : 'ACTIVE' } },
      {
        onSuccess: async () => {
          queryClient.invalidateQueries({ queryKey: ['bonuses'] });
          enqueueSnackbar(pause ? t('bonus:pauseSuccess') : t('bonus:continueSuccess'), {
            variant: 'detailedSuccess',
            details: pause
              ? t('bonus:pausedBonus', { bonus: info.row.original.title })
              : t('bonus:continuedBonus', { bonus: info.row.original.title })
          });
        },
        onError: (error: unknown) => {
          enqueueSnackbar(
            pause
              ? t('bonus:pauseError', {
                  error: error instanceof Error ? error.message : 'Unknown error'
                })
              : t('bonus:continueError', {
                  error: error instanceof Error ? error.message : 'Unknown error'
                }),
            {
              variant: 'error'
            }
          );
        }
      }
    );
  };

  const menuItems: ActionMenuItem[] = [
    {
      color: 'primary.main',
      icon: <PauseSquareIcon height={28} width={28} />,
      label: t('bonus:pause'),
      onClick: () => pause(true),
      hoverBgColor: 'grey.50',
      disabled: !['ACTIVE', 'WAITING'].includes(info.row.original.status ?? '')
    },
    {
      color: 'primary.main',
      icon: <ContinueSquareIcon height={28} width={28} />,
      label: t('bonus:continue'),
      onClick: () => pause(false),
      hoverBgColor: 'grey.50',
      disabled: info.row.original.status !== 'PAUSED'
    },
    {
      icon: <TrashSquareIcon height={28} width={28} />,
      label: t('bonus:cancel'),
      onClick: () => setShowDeleteDialog(true),
      color: 'error.main',
      hoverColor: 'error.dark',
      hoverBgColor: 'error.lightest',
      disabled: info.row.original.status === 'CANCELED'
    }
  ];

  return (
    <>
      <ActionsMenuCell
        items={menuItems}
        disabled={info.row.original.status === 'CANCELED' || isPayoutMonthInPast}
      />
      <ConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onCancel={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          deleteBonus({ id: info.row.original.id });
        }}
        title={t('bonus:cancelConfirmationTitle')}
        contentText={t('bonus:cancelConfirmationText', {
          bonus: info.row.original.title
        })}
        variant="delete"
      />
    </>
  );
}
